/**
 * Check your inbox and the verification link pages (Figma 10 · Check your
 * inbox 01–04, Link pages 01–02). `?email=` is the inbox page; `?token=` opens
 * the link: a good one signs in and lands on the remembered destination, an
 * ended one offers a new link, a used one says the address is verified.
 */
import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router";
import { CircleCheck, Hourglass, Link2Off, Mail, WifiOff } from "lucide-react";
import { useAuthStore } from "@/stores/authStore";
import { PublicShell } from "@/components/gettingIn/PublicShell";
import {
  Column,
  GiButton,
  GiField,
  Head,
  Note,
  Notice,
  QuietAction,
  StatusHead,
  Ways,
} from "@/components/gettingIn/parts";
import { clockTime, useResendClock } from "@/components/gettingIn/useResendClock";
import {
  EMAIL_PATTERN,
  authCalls,
  peekToken,
  readAuthFailure,
  takeDestination,
} from "@/components/gettingIn/authApi";
import type { PendingRegistration } from "../signup";

type LinkState = { kind: "checking" } | { kind: "ended"; email: string | null } | { kind: "verified"; email: string | null } | { kind: "invalid" } | { kind: "offline" };

export default function VerificationPage() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const email = params.get("email");
  if (token) return <VerificationLink token={token} />;
  if (email) return <CheckYourInbox email={email} />;
  return <LinkPage state={{ kind: "invalid" }} />;
}

/* ------------------------------------------------------------ the inbox */

function CheckYourInbox({ email }: { email: string }) {
  const navigate = useNavigate();
  const location = useLocation();
  const pending = (location.state as { pendingRegistration?: PendingRegistration } | null)?.pendingRegistration ?? null;
  const clock = useResendClock();
  const [resentAt, setResentAt] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const [offline, setOffline] = useState(false);
  const [changing, setChanging] = useState(false);

  const resend = async () => {
    setSending(true);
    setOffline(false);
    try {
      await authCalls.resendVerification(email);
      setResentAt(clockTime());
      clock.start();
    } catch (error) {
      if (readAuthFailure(error).kind === "offline") setOffline(true);
    } finally {
      setSending(false);
    }
  };

  if (changing && pending) {
    return (
      <ChangeAddress
        pending={pending}
        onBack={() => setChanging(false)}
        onSent={(next) => {
          setChanging(false);
          setResentAt(null);
          navigate(`/verify?email=${encodeURIComponent(next.email)}`, {
            replace: true,
            state: { pendingRegistration: next },
          });
        }}
      />
    );
  }

  return (
    <PublicShell still="network" title="Check your inbox" topAction={<QuietAction to="/login">Sign in</QuietAction>}>
      <Column>
        <StatusHead icon={Mail} title="Check your inbox">
          We sent a link to {email}. Open it on any device; it works for 24 hours.
        </StatusHead>
        {offline ? (
          <Notice tone="attention" icon={WifiOff}>
            Creo cannot be reached. Check your connection and resend.
          </Notice>
        ) : null}
        {clock.waiting ? (
          <Note>You can resend once a minute.</Note>
        ) : resentAt ? (
          <Note>Sent again at {resentAt}. Use the newest email.</Note>
        ) : null}
        <Ways>
          <GiButton
            variant="secondary"
            onClick={() => void resend()}
            loading={sending}
            disabled={clock.waiting}
            className={clock.waiting ? "!opacity-40" : undefined}
          >
            {clock.waiting ? `Resend in ${clock.label}` : "Resend email"}
          </GiButton>
        </Ways>
        <QuietAction
          onClick={() =>
            pending ? setChanging(true) : navigate(`/signup?email=${encodeURIComponent(email)}`)
          }
        >
          Wrong address?
        </QuietAction>
      </Column>
    </PublicShell>
  );
}

/** Check your inbox 04 (D8): the registration goes again with the new address. */
function ChangeAddress({
  pending,
  onBack,
  onSent,
}: {
  pending: PendingRegistration;
  onBack: () => void;
  onSent: (next: PendingRegistration) => void;
}) {
  const [email, setEmail] = useState(pending.email);
  const [error, setError] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
    inputRef.current?.select();
  }, []);

  const send = async () => {
    if (!EMAIL_PATTERN.test(email.trim())) return setError("That does not look like an email address.");
    setSending(true);
    setError(null);
    try {
      await authCalls.register(pending.name, email.trim(), pending.password);
      onSent({ ...pending, email: email.trim() });
    } catch (e) {
      const failure = readAuthFailure(e);
      setError(
        failure.kind === "offline"
          ? "Creo cannot be reached. Check your connection and try again."
          : failure.kind === "coded" && failure.code === "SIGNUP_NOT_ALLOWED"
            ? "This address has no invitation. Use the address your invitation went to."
            : failure.kind === "coded" && failure.code === "ACCOUNT_EXISTS"
              ? "An account already uses this address. Sign in instead."
              : "That address did not work. Check it and try again.",
      );
    } finally {
      setSending(false);
    }
  };

  return (
    <PublicShell still="network" title="Change the address" topAction={<QuietAction to="/login">Sign in</QuietAction>}>
      <Column>
        <Head title="Change the address" sub="We send the link to the new address." />
        <form
          className="flex w-full flex-col gap-6"
          noValidate
          onSubmit={(e) => {
            e.preventDefault();
            void send();
          }}
        >
          <GiField
            ref={inputRef}
            label="Email"
            type="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            error={error}
          />
          <GiButton type="submit" loading={sending}>
            Send to this address
          </GiButton>
        </form>
        <QuietAction onClick={onBack}>Back</QuietAction>
      </Column>
    </PublicShell>
  );
}

/* ------------------------------------------------------------ the link */

function VerificationLink({ token }: { token: string }) {
  const navigate = useNavigate();
  const login = useAuthStore((s) => s.login);
  const [state, setState] = useState<LinkState>({ kind: "checking" });
  const ran = useRef(false);

  useEffect(() => {
    // Once: a second run would answer "already verified" to our own success.
    if (ran.current) return;
    ran.current = true;
    authCalls
      .verify(token)
      .then((session) => {
        login(session.token, session.refreshToken ?? "");
        navigate(takeDestination() ?? "/dashboard", { replace: true });
      })
      .catch((error) => {
        const failure = readAuthFailure(error);
        const claimed = peekToken<{ email?: string }>(token)?.email ?? null;
        if (failure.kind !== "coded") return setState({ kind: "offline" });
        const email = typeof failure.data.email === "string" ? failure.data.email : claimed;
        if (failure.code === "LINK_ENDED") setState({ kind: "ended", email });
        else if (failure.code === "ALREADY_VERIFIED") setState({ kind: "verified", email });
        else setState({ kind: "invalid" });
      });
  }, [token, login, navigate]);

  return <LinkPage state={state} />;
}

function LinkPage({ state }: { state: LinkState }) {
  const navigate = useNavigate();
  const [sending, setSending] = useState(false);

  const sendNew = async (email: string) => {
    setSending(true);
    try {
      await authCalls.resendVerification(email);
      navigate(`/verify?email=${encodeURIComponent(email)}`, { replace: true });
    } finally {
      setSending(false);
    }
  };

  return (
    <PublicShell still="network" title="Verify your address" topAction={<QuietAction to="/login">Sign in</QuietAction>}>
      <Column>
        {state.kind === "checking" ? (
          <StatusHead icon={Mail} title="Opening your link" />
        ) : state.kind === "ended" ? (
          <>
            <StatusHead icon={Hourglass} title="This link ended">
              Verification links last 24 hours.{" "}
              {state.email ? `We can send a new one to ${state.email}.` : "Sign in to get a new one."}
            </StatusHead>
            <Ways>
              {state.email ? (
                <GiButton onClick={() => void sendNew(state.email!)} loading={sending}>
                  Send a new link
                </GiButton>
              ) : (
                <GiButton onClick={() => navigate("/login")}>Sign in</GiButton>
              )}
            </Ways>
          </>
        ) : state.kind === "verified" ? (
          <>
            <StatusHead icon={CircleCheck} title="Already verified">
              {state.email ?? "This address"} is verified. Sign in to carry on.
            </StatusHead>
            <Ways>
              <GiButton
                onClick={() => navigate(`/login${state.email ? `?email=${encodeURIComponent(state.email)}` : ""}`)}
              >
                Sign in
              </GiButton>
            </Ways>
          </>
        ) : state.kind === "offline" ? (
          <>
            <StatusHead icon={WifiOff} title="Creo cannot be reached">
              Your connection may be down, or we are. Open the link again in a moment.
            </StatusHead>
            <Ways>
              <GiButton onClick={() => window.location.reload()}>Try again</GiButton>
            </Ways>
          </>
        ) : (
          <>
            <StatusHead icon={Link2Off} title="This link does not open anything">
              It may have been copied only in part. Open it from the email again, or sign in.
            </StatusHead>
            <Ways>
              <GiButton onClick={() => navigate("/login")}>Sign in</GiButton>
            </Ways>
          </>
        )}
      </Column>
    </PublicShell>
  );
}
